import * as Colors from '@brightlayer-ui/colors';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, Divider, Theme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { PointOfContactFooter } from '../../components/PointOfContactFooter';
import { Spinner } from '../../components/Spinner';
import { StatusGaugeCard } from '../../components/StatusGaugeCard';
import { getUserRoles, UserSelectors } from '../../features/userSlice';
import { useAlert } from '../../lib/AlertContext';
import { Logger } from '../../logger';
import { OrganizationService } from '../../services/organization/organization.service';
import {
    ExternalApplication,
    GetUsersInOrganization,
    Organization,
    OrganizationSecretWithValue,
    OrganizationServiceAccount,
} from '../../services/organization/types';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { Subscription, Status as SubscriptionStatus } from '../../services/subscription/types';
import { OrganizationStatus, UserRole } from '../../services/user/types';
import { OrganizationId } from '../../types/idFlavors';
import { BLUISH_WHITE, GREENISH_WHITE } from '../../values/color';
import { ManageAppRegistration } from './ManageAppRegistration';
import OrgServiceAccountView from './OrgServiceAccountView';
import UserSection from './UserSection';
import { GenerateSecurityTokenConfirmationModal } from './UserSection/GenerateSecurityTokenConfirmationModal';

const styles = {
    sectionHeader: {
        marginY: '8px',
    },
    credentialsContainer: {
        marginTop: '16px',
    },
    main: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    container: {
        display: 'flex',
        flex: '1 0 auto',
        flexDirection: 'column',
        margin: '32px',
        padding: '8px',
        backgroundColor: Colors.white[50],
    },
    buttonContainer: (theme: Theme): object => ({
        paddingLeft: theme.spacing(3),
    }),
    addUserButton: {
        marginBottom: '16px',
        textTransform: 'uppercase',
    },
    button: {
        marginTop: '8px',
        marginBottom: '16px',
        textTransform: 'uppercase',
    },
    usersListContainer: {
        flexDirection: 'row',
        display: 'flex',
        justifyContent: 'space-between',
    },
    contentOrganizationBox: {
        alignContent: 'space-between',
        display: 'flex',
        flexDirection: 'row',
        flex: 0.5,
    },
    firstSection: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        margin: '24px',
        flexDirection: 'row',
    },
    divider: { marginInline: '24px' },
    sections: { margin: '24px' },
    createCredSection: {
        paddingLeft: '16px',
        border: '2px dashed #424E541F',
        borderRadius: '8px',
    },
    orgNameContainer: { marginBottom: '8px' },
    statusTagContainer: (backgroundColor: string): object => ({
        padding: '4px 8px',
        borderRadius: '2px',
        backgroundColor,
        marginBottom: '8px',
        display: 'inline-block',
    }),
    statusTagStyle: (fontColor: string): object => ({
        fontFamily: 'Open Sans',
        fontWeight: '600',
        fontSize: '12px',
        lineHeight: '100%',
        letterSpacing: '2px',
        textTransform: 'uppercase',
        textAlign: 'center',
        color: fontColor,
    }),
};

type OrganizationDetailsProps = {
    setShowError: (showError: boolean) => void;
    orgId: OrganizationId;
};

export const OrganizationDetails: React.FC<OrganizationDetailsProps> = (props) => {
    const { setShowError, orgId } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const organizations = useAppSelector(UserSelectors.userOrganizationRoles);
    // Find the organization by id from the Redux store
    const orgStatus = organizations?.find((org) => org.organizationId === orgId)?.organizationStatus;

    const [isLoading, setIsLoading] = useState(true);
    const [usersInOrg, setUsersInOrg] = useState<GetUsersInOrganization[]>([]);
    const [externalApp, setExternalApp] = useState<ExternalApplication>();
    const [orgServiceAccount, setOrgServiceAccount] = useState<OrganizationServiceAccount>();
    const [orgSecret, setOrgSecret] = useState<OrganizationSecretWithValue[]>();
    const [organization, setOrganization] = useState<Organization>();
    const [isCredentialCreated, setIsCredentialCreated] = useState(false);
    const [loadCredentials, setLoadCredentials] = useState(false);

    const currentUserId = useAppSelector((state) => UserSelectors.userDetails(state))?.id;
    const userRole = usersInOrg ? usersInOrg.find((user) => user.id === currentUserId)?.role : undefined;
    const isUserOwner = userRole && currentUserId && userRole === UserRole.OWNER;
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const { showErrorToUser } = useAlert();

    const [subscription, setSubscription] = useState<Subscription | undefined>(undefined);

    const getTagStyles = (label: OrganizationStatus | undefined): { backgroundColor: string; fontColor: string } => {
        switch (label) {
            case OrganizationStatus.REQUESTED:
                return {
                    backgroundColor: BLUISH_WHITE,
                    fontColor: Colors.blue[500],
                };
            case OrganizationStatus.APPROVED:
                return {
                    backgroundColor: GREENISH_WHITE,
                    fontColor: Colors.green[500],
                };
            default:
                return {
                    backgroundColor: Colors.white[400],
                    fontColor: Colors.darkBlack[900],
                };
        }
    };
    const tagStyles = getTagStyles(orgStatus);

    const createOrgServiceAccount = async (): Promise<void> => {
        try {
            const serviceAccountResponse = await OrganizationService.createOrgServiceAccount(orgId);
            if (serviceAccountResponse) {
                // Fetch the user roles again to update the state as after create credentials the user roles is changed to claimed
                await dispatch(getUserRoles());
                setOrgServiceAccount(serviceAccountResponse);
                setOrgSecret(serviceAccountResponse.clientSecrets);
                setShowConfirmationModal(true);
                setLoadCredentials(false);
                setIsCredentialCreated(true);
            }
        } catch {
            setLoadCredentials(false);
            showErrorToUser({
                errorTitle: t('CREATE_ORGANIZATION_PAGE.FAILED_TO_CREATE_ORG_CREDENTIALS_TITLE'),
                errorDescription: t('CREATE_ORGANIZATION_PAGE.FAILED_TO_CREATE_ORG_CREDENTIALS_BODY'),
                confirmText: t('COMMON.TRY_AGAIN'),
                cancelText: t('COMMON.CANCEL'),
                onConfirm: () => {
                    void createOrgServiceAccount();
                },
            });
        }
    };

    useEffect(() => {
        const fetchOrganizationDetailsData = async (): Promise<void> => {
            try {
                setIsLoading(true);

                const [fetchedOrg, fetchedUsers, fetchedSubscription, fetchedExternalApp] = await Promise.all([
                    OrganizationService.getOrganization(orgId),
                    OrganizationService.getUsersInOrganization(orgId),
                    orgStatus === OrganizationStatus.CLAIMED || orgStatus === OrganizationStatus.APPROVED
                        ? SubscriptionService.getSubscriptionOfOrganization(orgId)
                        : undefined,
                    // This returns `undefined` if the request fails so that everything else on the page is still accessible and only the 'Register' button is disabled
                    OrganizationService.getExternalApplication().catch(() => undefined),
                ]);
                setOrganization(fetchedOrg);
                setUsersInOrg(fetchedUsers);
                setSubscription(fetchedSubscription);
                setExternalApp(fetchedExternalApp);

                if (fetchedOrg.serviceAccount) {
                    setOrgServiceAccount(fetchedOrg.serviceAccount);
                    setIsCredentialCreated(true);
                }

                setShowError(false);
            } catch (error) {
                Logger.error(String(error));
                setShowError(true);
            } finally {
                setIsLoading(false);
            }
        };
        void fetchOrganizationDetailsData();
    }, [orgId]);

    if (isLoading || !organization) {
        return <Spinner testId={'org-details-loading-spinner'} />;
    }

    const getStatusGaugeColor = (status: SubscriptionStatus): string => {
        switch (status) {
            case SubscriptionStatus.ACTIVE:
                return Colors.green[500];
            case SubscriptionStatus.INACTIVE:
                return Colors.gray[500];
            case SubscriptionStatus.SUSPENDED:
                return Colors.orange[500];
            default:
                return Colors.red[500];
        }
    };

    return (
        <Box sx={styles.main}>
            <Box sx={styles.container}>
                {/* first section */}
                <Box sx={styles.firstSection}>
                    <Box sx={styles.contentOrganizationBox}>
                        <Box>
                            {(orgStatus === OrganizationStatus.APPROVED ||
                                orgStatus === OrganizationStatus.REQUESTED) && (
                                <Box sx={styles.statusTagContainer(tagStyles.backgroundColor)}>
                                    <Typography
                                        variant="overline"
                                        sx={styles.statusTagStyle(tagStyles.fontColor)}
                                        data-testid="organizationStatus-tag"
                                    >
                                        {orgStatus}
                                    </Typography>
                                </Box>
                            )}
                            <Typography variant="h6" sx={styles.orgNameContainer}>
                                {organization.name}
                            </Typography>
                            <Typography color={Colors.gray[500]}>{organization.description}</Typography>
                            {isUserOwner && (
                                <Box sx={{ ...styles.buttonContainer, marginTop: '8px' }}>
                                    <Button
                                        startIcon={<EditIcon />}
                                        sx={{ ...styles.button, marginTop: '4px', marginBottom: '0px' }}
                                        variant="outlined"
                                        color="primary"
                                        data-testid="edit-organization-button"
                                        onClick={(): void => {
                                            navigate(`/organizations/${organization.id}/edit`);
                                        }}
                                    >
                                        {t('ORGANIZATION_DETAILS_PAGE.EDIT_BUTTON')}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    {subscription && (
                        <Box
                            sx={{
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: Colors.white[400],
                                },
                            }}
                        >
                            <StatusGaugeCard
                                data-testid="subscription-status-card"
                                title={t('ORGANIZATION_DETAILS_PAGE.TIER')}
                                status={subscription.status}
                                subtitle1={subscription.tier}
                                subtitle2={t('ORGANIZATION_DETAILS_PAGE.SEE_LICENSE_DETAILS')}
                                color={getStatusGaugeColor(subscription.status)}
                                onClick={(): void =>
                                    navigate(`/subscriptionDetails`, {
                                        state: {
                                            subscriptionDetails: {
                                                licenseId: subscription.licenseId,
                                                organizationId: orgId,
                                                organizationName: organization.name,
                                                renewalDate: subscription.renewalDate,
                                                subscriptionStatus: subscription.status,
                                                subscriptionTier: subscription.tier,
                                                deviceLimit: subscription.deviceLimit,
                                                currentDeviceCount: subscription.currentDeviceCount,
                                                rateLimit: subscription.rateLimit,
                                                statusGaugeColor: getStatusGaugeColor(subscription.status),
                                            },
                                        },
                                    })
                                }
                            />
                        </Box>
                    )}
                </Box>
                <Divider sx={styles.divider} />
                {/* second section */}
                {orgServiceAccount ? (
                    <OrgServiceAccountView
                        orgServiceAccount={orgServiceAccount}
                        setOrgServiceAccount={setOrgServiceAccount}
                        organizationId={orgId}
                        organizationName={organization.name}
                        isUserOwner={!!isUserOwner}
                    />
                ) : (
                    <Box sx={styles.sections}>
                        <Box sx={styles.createCredSection}>
                            <Typography variant="h6" sx={styles.sectionHeader}>
                                {t('ORGANIZATION_DETAILS_PAGE.ORGANIZATION_CREDENTIALS')}
                            </Typography>
                            <Typography variant="body2" color={Colors.gray[500]} sx={styles.sectionHeader}>
                                {t('ORGANIZATION_DETAILS_PAGE.CREATE_SUBTEXT')}
                            </Typography>
                            <Button
                                sx={styles.button}
                                id={`create-credentials-button`}
                                variant="contained"
                                color="primary"
                                disabled={orgStatus !== OrganizationStatus.APPROVED || loadCredentials}
                                onClick={(): void => {
                                    setLoadCredentials(true);
                                    void createOrgServiceAccount();
                                }}
                            >
                                {loadCredentials ? (
                                    <CircularProgress sx={{ color: 'white' }} size={20} variant={'indeterminate'} />
                                ) : (
                                    t('ORGANIZATION_DETAILS_PAGE.CREATE_CREDENTIALS')
                                )}
                            </Button>
                        </Box>
                    </Box>
                )}
                <Divider sx={styles.divider} />
                {/* //third section */}
                <UserSection
                    organizationId={orgId}
                    usersInOrg={usersInOrg}
                    isUserOwner={!!isUserOwner}
                    setUsersInOrg={setUsersInOrg}
                    isCredentialCreated={!isCredentialCreated}
                />
                <Divider sx={styles.divider} />
                {/* //fourth section */}
                <Box sx={styles.sections}>
                    <ManageAppRegistration
                        isCredentialCreated={!isCredentialCreated}
                        organization={organization}
                        isUserOwner={!!isUserOwner}
                        externalApp={externalApp}
                        setExternalApp={setExternalApp}
                    />
                </Box>
            </Box>
            <PointOfContactFooter pointOfContact={organization.pointOfContact} />

            {showConfirmationModal && orgSecret && (
                <GenerateSecurityTokenConfirmationModal
                    setShowConfirmationModal={setShowConfirmationModal}
                    organizationSecret={orgSecret}
                    organizationName={organization.name}
                    organizationId={orgId}
                />
            )}
        </Box>
    );
};
